import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';

import { makeStyles } from '@material-ui/core';

import { getI18n } from "../../../utils/intl";
import { REPORT_CUSTOM } from '../../../constants/reports';
import * as Constants from '../../../constants/charts';

import {
  getDateRangeData, getDisplayDate, lineColors, lightShadeLineColors,
  clearMetricsHoverState, getCustomReportAggregation,
  getStartAndEndRangeForLastNDays,
} from '../utils';
import {
  getMetricKey, getSourcesIdsFromResponseForCustomReport,
  getSourceFromMetricKey, getMetricFromMetricKey,
  getDefaultSharedMutations,
  getBucketPermuteIdAndNamesForCustomReport,
} from '../utils/permute';
import {
  decodeCustomDateFilter, getSummaryFromMetricsFiltersForCustomReport,
  getSummaryFromDateFilter,
} from '../utils/filter';

import Loading from "../../Loading";
import NoData from '../NoData';
import ChartPanelFilterLabel from '../ChartPanelFilterLabel';
import ChartLegend from '../ChartLegend';
import CustomLabelComponent from '../CustomLabelComponent';
import CompositeXYChart from '../CompositeXYChart';
import PanelTitleDescription from '../PanelTitleDescription';
import CustomDateRangeFilter from '../CustomDateRangeFilter';

import { TooltipStyle } from '../ChartTooltip/index.theme';
import styles from './styles';

const CUSTOM_METRICS_CURRENT = 'current';

const getTimeData = (query, attr) => {
  let retVal = '';
  if (query && attr) {
    retVal = query[attr];
  }
  return retVal;
};

const getTimerange = timePeriodStr => {
  switch (timePeriodStr) {
    case '7D':
      return 'week';
    case '24H':
      return 'day';
    default:
      return 'custom';
  }
};

const timerangeMap = {
  HOUR_1: 'hour',
  HOUR_24: 'day',
  DAY_7: 'week',
};

const createCustomLabelComponent = styles => {
  const StyledCustomLabelComponent = CustomLabelComponent(styles);
  return <StyledCustomLabelComponent />;
};

const chartScale = { x: 'time' };

const ChartPanel = (props) => {
  const {
    title: titleProp, description: descriptionProp, selectedMetric, id,
    selectedDataSource, chartResp, yLabel, entitiesMap, updateDateFilters,
    apiList, appList, orgList, gatewayList, apiKeyDetailsMap, timeRange,
    editChartTitleDesc, openAddPanelModal,
  } = props;

  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const [state, updateState] = useState({
    title: titleProp,
    description: descriptionProp,
    sharedMutations: {},
  });

  const setState = (value) => {
    if (typeof value === 'function') {
      updateState(currentValue => ({ ...currentValue, ...value(state) }));
    } else {
      updateState(currentValue => ({ ...currentValue, ...value }));
    }
  }

  const titlePropRef = useRef(titleProp);
  useEffect(() => {
    if (titlePropRef.current !== titleProp) {
      setState({ title: titleProp });
    }
    titlePropRef.current = titleProp;
  }, [titleProp]);

  const descriptionPropRef = useRef(descriptionProp);
  useEffect(() => {
    if (descriptionPropRef.current !== descriptionProp) {
      setState({ description: descriptionProp });
    }
    descriptionPropRef.current = descriptionProp;
  }, [descriptionProp]);

  const selectedDataSourceRef = useRef(selectedDataSource);
  const entitiesMapRef = useRef(entitiesMap);
  useEffect(() => {
    if (chartResp?.data?.length) {
      const newSourcesIds = getSourcesIdsFromResponseForCustomReport(
        chartResp.data, CUSTOM_METRICS_CURRENT,
        entitiesMapRef.current, selectedDataSourceRef.current,
      );
      // reset sharedMutations if sources changes
      if (newSourcesIds.length > 0) {
        const sharedMutations = getDefaultSharedMutations(newSourcesIds, CUSTOM_METRICS_CURRENT);
        if (_.keys(sharedMutations).length) {
          setState({ sharedMutations });
        }
      }
    }
    entitiesMapRef.current = entitiesMap;
    selectedDataSourceRef.current = selectedDataSource;
  }, [chartResp, selectedDataSource, entitiesMap]);

  const getPointlevelData = (xAxisDates, dataPoints, dateRangeProps, tooltipLabel) => {
    const data = [];
    if (_.isUndefined(xAxisDates) || _.isUndefined(dataPoints)) {
      return data;
    }
    _.each(xAxisDates, (day, index) => {
      const date = moment(day);
      let value = 0;
      _.each(dataPoints, (item) => {
        if (date.isSame(moment(item.date))) {
          if (selectedMetric.value === 'latency') {
            value = item.avg;
          } else if (selectedMetric.value === 'hits') {
            value = item.count;
          }
        }
      });
      const { timerange, timeAgg, startDate, endDate } = dateRangeProps;
      const dateTimeStr = getDisplayDate(
        moment.utc(day).toISOString(),
        timerange,
        `by-${timeAgg}`,
        startDate,
        endDate,
        'tooltip',
      );
      data.push({
        x: index,
        y: value,
        label: `${tooltipLabel.join(' ❯ ')}\n\n${yLabel}: ${value}\n${getI18nStrings('dateTimeFormat')}:\n${dateTimeStr}`,
      });
    });
    return data;
  }

  const getChartData = (response, xAxisData, selectedDataSource) => {
    const chartData = [];
    if (_.isUndefined(response) || _.isUndefined(response.data) || _.isUndefined(xAxisData)) {
      return chartData;
    }
    let data = [];
    const dateRangeProps = getDateRangeProps(response);
    _.each(response.data, (combination, index) => {
      const entitiesArray = [];
      selectedDataSource.forEach(dataSource => {
        if (dataSource.isGrouped) {
          const groupedDimension = `${dataSource.dimension}-grouped`;
          entitiesArray.push(groupedDimension);
        } else if (dataSource.type !== 'default') {
          entitiesArray.push(dataSource.dimension);
        }
      });
      const entitiesStr = entitiesArray.join(',');
      const permuteObject = getBucketPermuteIdAndNamesForCustomReport(
        combination, entitiesStr, selectedDataSource, entitiesMap);
      const tooltipLabel = permuteObject.tooltipLabel.length ?
        permuteObject.tooltipLabel : permuteObject.names;
      data = getPointlevelData(xAxisData.xAxisTickValuesMap,
        combination.buckets, dateRangeProps, tooltipLabel);
      const combinationAggregation = selectedMetric.value === 'hits' ?
        combination.count : combination.avg;
      if (data.length && combinationAggregation && combination.buckets.length) {
        chartData.push({
          customLabelComponent: createCustomLabelComponent(TooltipStyle),
          id: permuteObject.permuteId,
          names: permuteObject.names,
          data,
          legendTooltipLabel: tooltipLabel,
          tooltipData: combinationAggregation,
          type: Constants.CHART_ELEMENT_LINE,
          colorIndex: index,
        });
      }
    });
    return chartData;
  };

  const getLineChartsData = (chartData, metric) => {
    const lineCharts = [];
    const { sharedMutations } = state;
    _.forEach(chartData, item => {
      const { id, data, names, customLabelComponent, tooltipData } = item;
      let { colorIndex } = item;
      const lineData = [];
      const sourceMutation = sharedMutations[id] || {};
      if (sourceMutation && sourceMutation.colorIndex !== undefined) {
        colorIndex = sourceMutation.colorIndex;
      }
      const metricsMutation = sourceMutation.metrics || {};

      const metricMutation = metricsMutation[metric] || {};
      const highLight = !!metricMutation.hover;
      if (!sourceMutation.show || !metricMutation.show) {
        return;
      }
      _.forEach(data, dataItem => {
        const { x, y, label } = dataItem;
        lineData.push({
          label,
          x,
          y,
        });
      });
      lineCharts.push({
        colorIndex,
        customLabelComponent,
        data: lineData,
        highLight,
        id,
        names,
        metric,
        tooltipData,
        type: Constants.CHART_ELEMENT_LINE,
      });
    });
    return lineCharts;
  };

  const getLegendData = (data) => {
    if (!data || !data.length) {
      return [];
    }
    const legendData = [];
    const { sharedMutations } = state;
    let label = '';
    if (selectedMetric.value === 'hits') {
      label = 'label.analytics.trafficChart.legend.apiHits.text';
    } else if (selectedMetric.value === 'latency') {
      label = 'label.analytics.latencyChart.apiResponseTime.text';
    }
    _.each(data, (item, index) => {
      const individualData = [];
      const { id, names, tooltipData, legendTooltipLabel } = item;
      const color = lineColors[index % lineColors.length];
      const sourceMutation = sharedMutations[id] || {};
      const sourceShow = !!sourceMutation.show;
      individualData.push({
        id: getMetricKey(id, CUSTOM_METRICS_CURRENT),
        label,
        value: tooltipData,
        component: getLegendColorStrip(color, true, sourceShow),
        show: sourceShow,
        disable: false,
      });
      legendData.push({
        uuid: id,
        names,
        color,
        data: individualData,
        show: sourceShow,
        legendTooltipLabel,
      });
    });
    return legendData;
  };

  const getLegendColorStrip = (color, isLine, show, pattern) => {
    const legendLineItemClass =
      pattern === 'dashed'
        ? classes.lineLegendItem__dashed
        : classes.lineLegendItem;
    return (<div
      className={
        isLine
          ? legendLineItemClass
          : classes.blockLegendItem
      }
      style={{
        backgroundColor: pattern === 'dashed' ? 'inherit' : color,
        visibility: show ? 'visible' : 'hidden',
        border: pattern === 'dashed' ? `2px dashed ${color}` : 'none',
      }}
    />);
  };

  const getI18nStrings = (key) => {
    let i18nString = '';
    if (!_.isUndefined(key)) {
      const strObj = {
        dateTimeFormat: 'label.analytics.date.time.format.text',
        noDataText: 'label.analytics.dashboard.noData.text',
        allApis: 'label.analytics.all.apis.string',
      };
      i18nString = intl.getI18nMessage(strObj[key]);
    }
    return i18nString;
  };

  const getDateRangeProps = (response) => {
    let dateRangeData = {};
    if (!_.isUndefined(response) && !_.isUndefined(response.query) && !_.isEmpty(response.query)) {
      dateRangeData = {
        timerange: getTimerange(getTimeData(response.query, 'period')),
        timeAgg: getTimeData(response.query, 'aggregation'),
        startDate: getTimeData(response.query, 'startDate'),
        endDate: getTimeData(response.query, 'endDate'),
      };
    }
    return dateRangeData;
  };

  const getDateParam = (dateObj) => {
    let aggregation;
    let timerange = 'custom';
    const newParams = {};
    if (!_.isUndefined(dateObj.customStartDate) && !_.isUndefined(dateObj.customEndDate)) {
      newParams.startDate = dateObj.customStartDate.format('YYYY-MM-DD');
      newParams.endDate = dateObj.customEndDate.format('YYYY-MM-DD');
      aggregation = getCustomReportAggregation(timerange, dateObj.customStartDate,
        dateObj.customEndDate);
    } else if (!_.isUndefined(dateObj.last) && _.isNumber(dateObj.last)) {
      const customDays = getStartAndEndRangeForLastNDays(dateObj.last);
      newParams.startDate = customDays.startDate;
      newParams.endDate = customDays.endDate;
      aggregation = getCustomReportAggregation(timerange, customDays.startDate, customDays.endDate);
    } else {
      timerange = timerangeMap[dateObj.last];
      aggregation = getCustomReportAggregation(timerange);
    }
    newParams.timerange = timerange;
    newParams.aggregation = aggregation;
    return newParams;
  }

  const handleDateRangeChange = customDateRange => {
    let timeRange = {};
    const customDates = customDateRange.customDates;
    if (customDates) {
      const startDateStr = customDates.startDate;
      const endDateStr = customDates.endDate;
      const interval = `${startDateStr}T00:00:00/${endDateStr}T23:59:59`;
      const startDate = Date.parse(startDateStr);
      const endDate = Date.parse(endDateStr);
      const aggregation = getCustomReportAggregation(
        moment(endDate).diff(startDate, 'days') + 1,
      );
      timeRange = {
        type: 'interval',
        interval,
        aggregation,
      };
    } else {
      const value = customDateRange.value;
      let inDays;
      let period;
      let isTextInput = false;
      switch (value) {
        case 'DAY_7':
          period = '7D';
          inDays = 7;
          break;
        case 'HOUR_24':
          period = '24H';
          inDays = 1;
          break;
        default:
          period = `${value}D`;
          inDays = value;
          isTextInput = true;
      }
      timeRange = {
        type: 'period',
        period,
        isTextInput,
        aggregation: getCustomReportAggregation(inDays),
      };
    }
    updateDateFilters(timeRange, id);
  };

  const handleTitleDescChange = titleDescObj => {
    if (!_.isEmpty(titleDescObj)) {
      if (!_.isEmpty(titleDescObj.title)) {
        setState({ title: titleDescObj.title.trim() });
      }
      if (!_.isNull(titleDescObj.description)) {
        setState({ description: titleDescObj.description.trim() });
      }
      editChartTitleDesc(titleDescObj, id);
    }
  };

  const handleDataSourceClick = (dimensionId) => {
    openAddPanelModal(
      id,
      dimensionId,
      selectedDataSource,
    );
  }

  const chartHoverHandler = (id, metric, hoverState) => {
    legendHoverHandler(getMetricKey(id, metric), hoverState);
  };

  const legendHoverHandler = (key, hoverState) => {
    setState(prevState => {
      let sharedMutations = prevState.sharedMutations || {};
      const source = getSourceFromMetricKey(key);
      const sourceMutation = (sharedMutations && sharedMutations[source]) || {};
      const metric = getMetricFromMetricKey(key);
      const metricsMutation =
        (sourceMutation && sourceMutation.metrics) || {};
      if (hoverState) {
        // DE437921: we started using hover state to enable/disable tooltip
        // But, with VictoryVoronoiContainer data points from different metrics
        // can be in hover state, when they have same values. so, i am
        // clearing hover state of all metrics to keep only the latest one
        // as highlighted(i.e. only it's tooltips will be active).
        // so user would see only 1 tooltip
        sharedMutations = clearMetricsHoverState(sharedMutations);
      }
      const metricMutation =
        (metricsMutation && metricsMutation[metric]) || {};
      const updatedMutations = {
        ...sharedMutations,
        [source]: {
          ...sourceMutation,
          metrics: {
            ...metricsMutation,
            [metric]: {
              ...metricMutation,
              hover: hoverState,
            },
          },
        },
      };
      return {
        sharedMutations: updatedMutations,
      };
    });
  };

  const legendClickHandler = (key, metricEvent) => {
    const { timerange, timeAgg, startDate, endDate } = getDateRangeProps(chartResp);
    const dateRangeData = getDateRangeData(
      timerange,
      timeAgg,
      startDate,
      endDate,
    );
    const chartData = getChartData(chartResp, dateRangeData, selectedDataSource);
    const colorIndex = chartData.find(
      data => {
        const id = key.includes('___') ? key.substring(0, key.indexOf('___')) : key;
        return data.id === id;
      },
    ).colorIndex;
    setState(prevState => {
      const sharedMutations = prevState.sharedMutations || {};
      const sourceKey = metricEvent ? getSourceFromMetricKey(key) : key;
      const metricKey = getMetricFromMetricKey(key);
      const sourceMutation = (sharedMutations && sharedMutations[sourceKey]) || {};
      const metricsMutation = sourceMutation.metrics || {};
      let updatedMutations = sharedMutations;
      if (metricEvent) {
        const metric = getMetricFromMetricKey(key);
        if (metric === CUSTOM_METRICS_CURRENT) {
          updatedMutations = {
            ...sharedMutations,
            [sourceKey]: {
              ...sourceMutation,
              show: !sourceMutation.show,
              metrics: {
                [metricKey]: { show: !sourceMutation.show },
              },
            },
          };
        } else {
          const metricMutation = metricsMutation[metricKey] || {};
          updatedMutations = {
            ...sharedMutations,
            [sourceKey]: {
              show: true,
              metrics: {
                ...metricsMutation,
                sum: { show: true },
                [metricKey]: {
                  show: !metricMutation.show,
                },
              },
              colorIndex,
            },
          };
        }
      }
      return {
        sharedMutations: updatedMutations,
      };
    });
  };


  const { title, description } = state;
  const isLoading = chartResp ? chartResp.loading : false;
  const { timerange, timeAgg, startDate, endDate } = getDateRangeProps(chartResp);
  const dateRangeData = getDateRangeData(
    timerange,
    timeAgg,
    startDate,
    endDate,
  );
  const chartData = getChartData(chartResp, dateRangeData, selectedDataSource);
  const lineChartData = getLineChartsData(chartData, CUSTOM_METRICS_CURRENT);
  const legendData = getLegendData(chartData);

  const getXAxisFormatter = (item, dateRangeObj) => {
    const xAxisTickValuesMap = dateRangeObj.xAxisTickValuesMap;
    if (xAxisTickValuesMap[item]) {
      return getDisplayDate(
        xAxisTickValuesMap[item],
        dateRangeObj.dateRange,
        `by-${dateRangeObj.timeAgg}`,
        dateRangeObj.startDate,
        dateRangeObj.endDate,
      );
    }
    return '';
  };

  const renderFilterForPrintSummary = () => {
    const summaryTextLines = getSummaryFromMetricsFiltersForCustomReport(
      selectedDataSource, entitiesMap, intl);
    return summaryTextLines ?
    (<div className={classes.printdataContainer}>
      {_.map(summaryTextLines,
        (line, index) => <div key={index} className={classes.printdatafilterelem}>{line}</div>)
      }
    </div>) :
    null;
  };

  const renderDateFilterForPrintSummary = () => {
    const decodedDateFilter = decodeCustomDateFilter(timeRange);
    const dateObj = getDateParam(decodedDateFilter);
    const dateFilterSummary = getSummaryFromDateFilter(dateObj, intl);
    return (<div className={classes.printdateContainer}>
      <div className={classes.smallLabel} >
        {intl.getI18nMessage('label.analyticsDashboard.filter.dateRange.label')}
      </div>
      <div className={classes.printDateSummary}>{dateFilterSummary}</div>
    </div>);
  };

  const renderPanelHeader = () => (
    <div className={classes.panelHeader}>
      <PanelTitleDescription
        title={title}
        description={description}
        handleTitleDescChange={handleTitleDescChange}
      />
      <div className={'SkipPrint'}>
        <div className={classes.filterContainer}>
          <div className={classes.metricFilter}>
            <div className={classes.metricLabel}>
              {intl.getI18nMessage('label.analyticsDashboard.customReport.chartPanel.metric.label')}
            </div>
            <div className={classes.label}>{selectedMetric.text}</div>
          </div>
          <div className={classes.dataSourceFilter}>
            <div className={classes.smallLabel}>
              {intl.getI18nMessage('label.analytics.customReport.dashboard.label.dataSource.text')}
            </div>
            <ChartPanelFilterLabel
              handleClick={handleDataSourceClick}
              data-apim-test={'data-source-btn'}
              filterLabels={selectedDataSource}
              apiList={apiList}
              apiKeyDetailsMap={apiKeyDetailsMap}
              appList={appList}
              orgList={orgList}
              gatewayList={gatewayList}
            />
          </div>
          <div className={classes.dateFilter}>
            <div className={classes.dateFilterContainer} data-apim-test={'date-drop-down'}>
              <CustomDateRangeFilter
                handleDateRangeChange={handleDateRangeChange}
                hideLastHourOption
                reportType={REPORT_CUSTOM}
                selectedDate={decodeCustomDateFilter(timeRange)}
                largeButton
              />
            </div>
          </div>
        </div>
      </div>
      <div className={classes.printfilterContainer}>
        <div className={classes.printdataSourceFilter}>
          <div className={classes.smallLabel}>
            {intl.getI18nMessage('label.analytics.customReport.dashboard.label.dataSource.text')}
          </div>
          <div className={classes.printfiltersummary}>
            {renderFilterForPrintSummary()}
          </div>
        </div>
        <div className={classes.metricFilter}>
          <div className={classes.metricLabel}>
            {intl.getI18nMessage('label.analyticsDashboard.customReport.chartPanel.metric.label')}
          </div>
          <div className={classes.printMetricName}>
            {selectedMetric.text}
          </div>
        </div>
        <div className={classes.dateFilter}>
          <div className={classes.printDateFilterSummary}>
            {renderDateFilterForPrintSummary()}
          </div>
        </div>
      </div>
    </div>
  );

  const renderChartComponent = () => (
    <div>
      { isLoading ? (
        <div className={classes.loader}>
          <Loading />
        </div>
        ) : (
          <div className={classes.chartContainer}>
            <div className={classes.chartBody}>
              <CompositeXYChart
                chartData={lineChartData}
                chartScale={chartScale}
                formatXAxisData={(item) => getXAxisFormatter(item, dateRangeData)}
                lineColors={lineColors}
                lightShadeLineColors={lightShadeLineColors}
                xAxisLabel={getI18nStrings('dateTimeFormat')}
                xAxisValues={dateRangeData.xAxisTickValues}
                yAxisLabel={yLabel}
                hoverHandler={chartHoverHandler}
              />
            </div>
            <ChartLegend
              data={legendData}
              hoverHandler={legendHoverHandler}
              clickHandler={legendClickHandler}
              showUngroupedDataOnTooltip
            />
          </div>
        )
      }
    </div>
  );

  return (
    <div className={classes.chartPanelContainer}>
      { renderPanelHeader() }
      { !chartData.length && !isLoading ? (
        <NoData />
        ) : (
        renderChartComponent()
        )
      }
    </div>
  );
}

ChartPanel.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  chartResp: PropTypes.object,
  yLabel: PropTypes.string,
  selectedMetric: PropTypes.object,
  selectedDataSource: PropTypes.array,
  timeRange: PropTypes.object,
  entitiesMap: PropTypes.object,
  openAddPanelModal: PropTypes.func,
  editChartTitleDesc: PropTypes.func,
  updateDateFilters: PropTypes.func,
  apiList: PropTypes.object,
  apiKeyDetailsMap: PropTypes.object,
  appList: PropTypes.object,
  orgList: PropTypes.object,
  gatewayList: PropTypes.object,
};

export default ChartPanel;